import log from 'loglevel';
import assign from 'lodash/assign';
import modalHTML from './templates/modalTemplate.html';
import classes from '../styles/modal.scss'; // This will export an object with class names required.
import i18n from './helpers/i18n';

/**
 * Function to inject the geolocation utility into the
 * global navigation and footer.
 * @author brandonp@criticalmass.com
 * @param  {Element} navEl    Element to bind navigation to.
 * @param  {Element} footerEl Element to bind footer to.
 * @param  {Object}  options  Object containing options to override defaults.
 */
export default function(navEl, footerEl, options) {

  var brand  = process.env.brand;
  var isNissan = ['default', 'nissan'].indexOf(brand) !== -1;
  var defaults = {};
  var nav, footer, modal, navItem, $html, errorContainer;
  var currentLocation = window.CM.NIFTI.GEOLOCATIONHELPER.promiseComplete;
  var geolocator = window.CM.NIFTI.GEOLOCATION.geolocator;
  const DTM = window.CM.NIFTI.DTM;

  (function init() {
    options = assign(defaults, options);
    nav = navEl || document.querySelector('.nna-navigation-standalone-base[data-geolocation=true]');
    footer = footerEl || document.querySelector('#nna-footer[data-geolocation=true]');
    $html = document.querySelector('html');
    if(isNissan && nav) {
      injectNissanNav();
    } else if(!isNissan && footer) {
      injectInfinitiFooter();
    } else {
      log.debug("GelocationUI: Global Nav and/or footer not found on page and/or doesn't contain the [data-geolocation=true] flag.");
      return false;
    }
    injectModal();
    geolocator.subscribe(function(data) {
      log.debug("GelocationUI: Got updated data from geolocation service.", data);
      setModalContent(data.geoData);
    });
  })();

  /**
   * Function to open the geolocation modal,
   * also loads the nessessary css and html if needed.
   */
  function openGeolocationModal() {
    log.info("GelocationUI: Opening Geolocation modal");
    // load if needed
    if(!modal) {
      injectModal();
    }
    // remove hidden class
    modal.classList.remove(classes.hidden);
    // add event listeners
    toggleModalEventListeners(true);
    // prevent scrolling
    $html.classList.add(classes.overflowHidden);
    // dtm
    DTM.NnaDtmTracking().directCall('event.modal', {name: 'update-your-location', section: 'geolocation', action: 'open'}, 'dtmModalOpen');
    // because this could be from an event
    return false;
  }

  /**
   * Function to close modal and remove event listeners
   */
  function closeGeolocationModal() {
    modal.classList.add(classes.hidden);
    toggleModalEventListeners(false);
    $html.classList.remove(classes.overflowHidden);
    DTM.NnaDtmTracking().directCall('event.modal', {name: 'update-your-location', section: 'geolocation', action: 'close'}, 'dtmModalOpen');
  }

  /**
   * Function for enabling/disabling event listeners based on modal open or closed.
   * Assumes modal already instantiated.
   * @param  {Boolean} isModalOpen is modal opening
   */
  function toggleModalEventListeners(isModalOpen) {
    var closeButtons = modal.querySelectorAll('[data-close]');
    var locate = modal.querySelector('[data-fetch-location]');
    var form = modal.querySelector('[data-set-location]');
    if(isModalOpen) {
      [].forEach.call(closeButtons, (closeButton) => closeButton.addEventListener('click', closeGeolocationModal));
      // closeButtons.forEach(closeButton => closeButton.addEventListener('click', closeGeolocationModal));
      locate.addEventListener('click', fetchGeolocation);
      form.addEventListener('submit', setGeolocation);
    } else {
      [].forEach.call(closeButtons, (closeButton) => closeButton.removeEventListener('click', closeGeolocationModal));
      locate.removeEventListener('click', fetchGeolocation);
      form.removeEventListener('submit', setGeolocation);
    }
  }

  /**
   * Function to reinitialize the geolocation utility and update
   * modal form fields.
   */
  function fetchGeolocation() {
    modal.classList.add(classes.loading);
    geolocator.locate().then(function(location) {
      errorContainer.classList.add(classes.hide);
      setModalContent(location);
    }).catch(function(e) {
      handleLocationError();
      errorContainer.classList.remove(classes.hide);
      errorContainer.querySelector('span').innerText = i18n('modal.errors.unavailable');
    });
  }

  /**
   * Function to set the geolocation utility
   * @param {Event} e Element which triggered the change. Should be a <form> element.
   */
  function setGeolocation(e) {
    e.preventDefault();
    var validate = validateUserGeolocation(e.target);
    if(validate.valid) {
      DTM.NnaDtmTracking().directCall('event.form', {name: 'update-your-location', section: 'geolocation', error: ''}, 'dtmSubmitLocationSuccess');
      geolocator.set(validate.values);
      closeGeolocationModal();
    } else {
      DTM.NnaDtmTracking().directCall('event.form', {name: 'update-your-location', section: 'geolocation', error: 'zipCode'}, 'dtmSubmitLocationError');
    }
  }

  /**
   * Function to validate form and/or display errors if not valid.
   * @param  {Element} form a <form> element to validate against
   * @return {Object} object containing {Boolean} valid and {Object} of values
   */
  function validateUserGeolocation(form) {
    // Get Elements
    var zipCode = form.querySelector('[data-zip-code]');
    var city = form.querySelector('[data-city]');
    var state_abbreviated = form.querySelector('[data-state]');
    // Get Values
    var values = {
      zipCode: zipCode.value,
      city: city.value || null,
      state_abbreviated: state_abbreviated.value || null,
      country_abbreviated: 'US',
      country: 'United States'
    };
    // Test Valid ZIP
    var valid = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.zipCode);
    // Remove error classes
    errorContainer.classList.add(classes.hide);
    zipCode.classList.remove(classes.invalid);
    // Add or remove error classes
    if(!valid) {
      // add error classes
      errorContainer.classList.remove(classes.hide);
      errorContainer.querySelector('span').innerText = i18n('modal.errors.invalid');
      zipCode.classList.add(classes.invalid);
    }
    return {valid, values};
  }

  /**
   * Function to handle location error. Updates nav items,
   * and disables locator in modal.
   */
  function handleLocationError() {
    [].forEach.call(navItem, (ni) => ni.querySelector('span').innerText = i18n('nav.unavailable'));
    modal.classList.remove(classes.loading);
    modal.querySelector('[data-locator-container]').classList.add(classes.hide);
  }

  /**
   * Function to set the modal content
   * @param  {Object} location Prepopulated geoLocation object (usually from CM.NIFTI.GEOLOCATION.promiseComplete)
   */
  function setModalContent(location) {
    if(modal && location) {
      if(location.geoData) location = location.geoData;
      log.debug("GelocationUI: Updating geolocation modal with content:", location);
      // populate form
      modal.querySelector('[data-zip-code]').value = location.zipCode || '';
      modal.querySelector('[data-city]').value = location.city || '';
      modal.querySelector('[data-state]').value = location.state_abbreviated || '';
      // add new zip to nav
      [].forEach.call(navItem, (ni) => ni.querySelector('span').innerText = location.zipCode || i18n('nav.unavailable'));
      // remove loading indicator
      modal.classList.remove(classes.loading);
    }
  }


  /**
   * Function to inject the modal HTML onto the page
   */
  function injectModal() {
    document.querySelector('body').insertAdjacentHTML('beforeend', modalHTML({i18n, classes}));
    modal = document.querySelector(`.${classes.modal}`); // Reset element
    errorContainer = modal.querySelector('[data-error-container]');
  }

  /**
   * Function to add an event listener to open the
   * geolocation modal.
   */
  function injectNissanNav() {
    navItem = [nav.querySelector('ul.menu-extra li .current-location')];
    // Add event listener
    [].forEach.call(navItem, (ni) => ni.addEventListener('click', openGeolocationModal));
    // On location, update text
    currentLocation.then(setModalContent).catch(handleLocationError);
  }

  /**
   * Function to add an event listener to open the
   * geolocation modal.
   */
  function injectInfinitiFooter() {
    navItem = footer.querySelectorAll('.current-location');
    // Add event listener
    [].forEach.call(navItem, (ni) => ni.addEventListener('click', openGeolocationModal));
    // on location, upadte text
    currentLocation.then(setModalContent).catch(handleLocationError);
  }

}


// WEBPACK FOOTER //
// ./src/scripts/geolocator-injector.js